import { ActionTypes as CommonActionTypes, trackGoogleAnalyticsPageview } from 'common/actions'
import { ActionTypes as CommonApplicationActionTypes } from 'common/actions/application'
import { CARD_PURPOSES_URL_KEY, LifeInsuranceResultsView, RESULTS_URL_KEY } from 'embed/constants'
import Parent from 'common/services/Parent'
import { LoanStep, Product, QueryParamKeys, TrackingKey } from 'even-ts-static'

// Action type keys
export enum ActionTypeKeys {
  ANCHOR_CLICK = 'ANCHOR_CLICK',
  PRODUCT_SELECTED = 'PRODUCT_SELECTED',
  SET_AVAILABLE_PRODUCTS = 'SET_AVAILABLE_PRODUCTS',
  SET_LIFE_INSURANCE_OFFER = 'SET_LIFE_INSURANCE_OFFER',
  SET_LIFE_INSURANCE_RESULTS_VIEW = 'SET_LIFE_INSURANCE_RESULTS_VIEW',
  SET_LIFE_INSURANCE_COVERAGE_AMOUNT = 'SET_LIFE_INSURANCE_COVERAGE_AMOUNT',
  SET_LIFE_INSURANCE_COVERAGE_DURATION = 'SET_LIFE_INSURANCE_COVERAGE_DURATION',
  SET_VIEWPORT_SIZE = 'SET_VIEWPORT_SIZE',
}

// Action types
interface ProductSelected {
  type: ActionTypeKeys.PRODUCT_SELECTED
  product: Product
}

interface SetAvailableProducts {
  type: ActionTypeKeys.SET_AVAILABLE_PRODUCTS
  products: Product[]
  trackingKey: TrackingKey.AvailableProducts
  trackingData: object
}

interface AnchorClick {
  trackingData: {
    href: string
  }
  trackingKey: TrackingKey.AnchorClick
  type: ActionTypeKeys.ANCHOR_CLICK
}

export interface SetLifeInsuranceOffer {
  type: ActionTypeKeys.SET_LIFE_INSURANCE_OFFER
}

export interface SetLifeInsuranceResultsView {
  resultsView: LifeInsuranceResultsView
  type: ActionTypeKeys.SET_LIFE_INSURANCE_RESULTS_VIEW
  trackingKey: TrackingKey.LifeInsuranceResultsViewSet
  trackingData: object
}

export interface SetLifeInsuranceCoverageAmount {
  coverageAmount: number
  type: ActionTypeKeys.SET_LIFE_INSURANCE_COVERAGE_AMOUNT
}

export interface SetLifeInsuranceCoverageDuration {
  coverageDuration: number
  type: ActionTypeKeys.SET_LIFE_INSURANCE_COVERAGE_DURATION
}

interface SetViewportSize {
  height: number
  width: number
  type: ActionTypeKeys.SET_VIEWPORT_SIZE
}

export type ActionTypes =
  | AnchorClick
  | CommonActionTypes
  | CommonApplicationActionTypes
  | ProductSelected
  | SetAvailableProducts
  | SetLifeInsuranceOffer
  | SetLifeInsuranceResultsView
  | SetLifeInsuranceCoverageAmount
  | SetLifeInsuranceCoverageDuration
  | SetViewportSize

// Action creators
export const productSelected = (product: Product) => (dispatch) => {
  dispatch(trackGoogleAnalyticsPageview(product))
  dispatch({
    product,
    type: ActionTypeKeys.PRODUCT_SELECTED,
  })
}

export const setAvailableProducts = (products: Product[]): SetAvailableProducts => ({
  products,
  trackingData: { products },
  trackingKey: TrackingKey.AvailableProducts,
  type: ActionTypeKeys.SET_AVAILABLE_PRODUCTS,
})

export const appendCardPurposesToParentUrl = (cardPurpose: string) => (dispatch, getState) =>
  Parent.updateUrl(CARD_PURPOSES_URL_KEY, cardPurpose)

export const appendUuidToParentUrl = (uuid: string) => (dispatch, getState) => {
  Parent.updateUrl(RESULTS_URL_KEY, uuid)
  Parent.updateUrl(QueryParamKeys.Step, LoanStep.Results)
}

export const lifeInsuranceOfferLoaded = () => ({
  type: ActionTypeKeys.SET_LIFE_INSURANCE_OFFER,
})

export const setLifeInsuranceResultsView = (resultsView: LifeInsuranceResultsView) => ({
  resultsView,
  type: ActionTypeKeys.SET_LIFE_INSURANCE_RESULTS_VIEW,
  trackingKey: TrackingKey.LifeInsuranceResultsViewSet,
  trackingData: {
    resultsView,
  },
})

export const setLifeInsuranceCoverageAmount = (coverageAmount: number) => ({
  coverageAmount,
  type: ActionTypeKeys.SET_LIFE_INSURANCE_COVERAGE_AMOUNT,
})

export const setLifeInsuranceCoverageDuration = (coverageDuration: number) => ({
  coverageDuration,
  type: ActionTypeKeys.SET_LIFE_INSURANCE_COVERAGE_DURATION,
})

export const setViewportSize = (height: number, width: number) => ({
  height,
  type: ActionTypeKeys.SET_VIEWPORT_SIZE,
  width,
})

export const anchorClick = (href: string): AnchorClick => ({
  trackingData: {
    href,
  },
  trackingKey: TrackingKey.AnchorClick,
  type: ActionTypeKeys.ANCHOR_CLICK,
})
